/**
 * LegalGraphTab.tsx — v2
 * ─────────────────────────────────────────────────────────────────
 * Onglet "Graphe" — Legal Graph complet SmartLawyer
 *
 * Nœuds :
 *   center    — cet arrêt
 *   cite      — arrêts cités qualifiés (revirement, application, distinction…)
 *   cited_by  — arrêts qui citent cet arrêt
 *   procedure — arrêt attaqué, CA de renvoi, pourvois joints (procedure_graph)
 *   impacte   — jurisprudence antérieure renversée / modifiée (arrets_impactes)
 *   article   — articles de loi visés
 *
 * Liens :
 *   revirement | confirmation | application_principe | distinction |
 *   reference_jurisprudentielle | obiter_dictum | meme_affaire |
 *   casse | rejette | cassation_sans_renvoi | renvoi_apres_cassation |
 *   jonction | rapprochement | cited_by | impact_revirement |
 *   impact_restriction | impact_extension | article_vise
 *
 * Installation : pnpm add react-force-graph-2d
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { PublicFiche, Relations } from './types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LegalGraphTabProps {
  fiche:     PublicFiche;
  relations: Relations;
}

type NodeGroup = 'center' | 'cite' | 'cited_by' | 'procedure' | 'impacte' | 'article';

interface GNode {
  id:        string;
  label:     string;
  group:     NodeGroup;
  size:      number;
  // Métadonnées optionnelles
  date?:     string;
  chamber?:  string;
  solution?: string;
  probleme?: string;
  slug?:     string;
  // Pour articles
  code?:     string;
  article?:  string;
  // Pour liens cités : type de citation
  citeType?: string;
  // Pour arrets_impactes : type d'impact
  impact?:   string;
  explication?: string;
}

interface GLink {
  source:  string;
  target:  string;
  label:   string;
  group:   string;
  color:   string;
  width?:  number;
  dashed?: boolean;
}

// ── Palettes ──────────────────────────────────────────────────────────────────

const NODE_COLOR: Record<NodeGroup, { fill: string; stroke: string; glow: string }> = {
  center:    { fill: '#c4b5fd', stroke: '#7c3aed', glow: '#7c3aed40' },
  cite:      { fill: '#93c5fd', stroke: '#1d4ed8', glow: '#1d4ed830' },
  cited_by:  { fill: '#6ee7b7', stroke: '#059669', glow: '#05966930' },
  procedure: { fill: '#fdba74', stroke: '#c2410c', glow: '#c2410c30' },
  impacte:   { fill: '#fca5a5', stroke: '#b91c1c', glow: '#b91c1c40' },
  article:   { fill: '#fde68a', stroke: '#d97706', glow: '#d9770630' },
};

// Couleurs des liens selon leur type sémantique
const LINK_COLOR: Record<string, string> = {
  // Citations qualifiées
  revirement:                   '#ef4444',  // rouge vif — rupture jurisprudentielle
  confirmation:                 '#22c55e',  // vert — renforcement
  application_principe:         '#3b82f6',  // bleu — application standard
  distinction:                  '#a855f7',  // violet — nuance
  reference_jurisprudentielle:  '#6366f1',  // indigo — grand arrêt
  obiter_dictum:                '#94a3b8',  // gris — incidentel
  meme_affaire:                 '#f59e0b',  // ambre — même affaire
  // Procéduraux
  casse:                        '#f97316',  // orange — cassation
  rejette:                      '#84cc16',  // vert clair — rejet
  cassation_sans_renvoi:        '#fb923c',  // orange clair
  cassation_partielle:          '#fbbf24',  // ambre
  renvoi_apres_cassation:       '#fbbf24',  // ambre
  jonction:                     '#67e8f9',  // cyan — pourvois joints
  rapprochement:                '#c084fc',  // violet clair — curé CC
  // Citants
  cited_by:                     '#34d399',  // vert menthe
  // Impacts
  impact_revirement:            '#dc2626',  // rouge foncé
  impact_restriction:           '#f59e0b',  // ambre
  impact_extension:             '#06b6d4',  // cyan
  impact_confirmation_renforcee:'#16a34a',  // vert foncé
  // Articles
  article_vise:                 '#f59e0b',  // doré
  // Fallback
  default:                      '#64748b',
};

const LINK_WIDTH: Record<string, number> = {
  revirement:    3,
  cassation_sans_renvoi: 2.5,
  casse:         2.5,
  impact_revirement: 3,
  rapprochement: 2,
  default:       1.5,
};

// Libellés courts pour les liens (clés i18n, résolues au rendu via t())
const LINK_LABEL: Record<string, string> = {
  revirement:                   'graph.link.revirement',
  confirmation:                 'graph.link.confirmation',
  application_principe:         'graph.link.application_principe',
  distinction:                  'graph.link.distinction',
  reference_jurisprudentielle:  'graph.link.reference_jurisprudentielle',
  obiter_dictum:                'graph.link.obiter_dictum',
  meme_affaire:                 'graph.link.meme_affaire',
  casse:                        'graph.link.casse',
  rejette:                      'graph.link.rejette',
  cassation_sans_renvoi:        'graph.link.cassation_sans_renvoi',
  cassation_partielle:          'graph.link.cassation_partielle',
  renvoi_apres_cassation:       'graph.link.renvoi_apres_cassation',
  jonction:                     'graph.link.jonction',
  rapprochement:                'graph.link.rapprochement',
  cited_by:                     'graph.link.cited_by',
  impact_revirement:            'graph.link.impact_revirement',
  impact_restriction:           'graph.link.impact_restriction',
  impact_extension:             'graph.link.impact_extension',
  impact_confirmation_renforcee:'graph.link.impact_confirmation_renforcee',
  article_vise:                 'graph.link.article_vise',
};

// ── Builder de graphe ─────────────────────────────────────────────────────────

function buildGraph(fiche: PublicFiche, relations: Relations, t: TFunction) {
  const nodes: GNode[] = [];
  const links: GLink[] = [];
  const seen  = new Set<string>();

  const addNode = (n: GNode) => {
    if (!seen.has(n.id)) { seen.add(n.id); nodes.push(n); }
  };

  const addLink = (l: GLink) => {
    // Éviter les doublons source↔target
    const key = `${l.source}→${l.target}→${l.group}`;
    if (!seen.has(key)) { seen.add(key); links.push(l); }
  };

  const linkColor = (group: string) => LINK_COLOR[group] ?? LINK_COLOR.default;
  const linkWidth = (group: string) => LINK_WIDTH[group] ?? LINK_WIDTH.default;
  const linkLabel = (group: string) => (LINK_LABEL[group] ? t(LINK_LABEL[group]) : group);

  // ── Nœud central ──────────────────────────────────────────────────────────
  addNode({
    id: fiche.id, label: fiche.number || fiche.ecli,
    group: 'center', size: 16,
    date: fiche.decision_date, chamber: fiche.chamber,
    solution: fiche.solution, probleme: fiche.probleme,
  });

  // ── Articles visés ─────────────────────────────────────────────────────────
  // On regroupe les articles par code pour éviter la surcharge visuelle
  const articlesSeen = new Set<string>();
  for (const art of fiche.articles || []) {
    if (!art?.code || !art?.article) continue;
    // Regrouper les articles du même code
    const codeId = `art_code_${art.code.replace(/\s+/g, '_')}`;
    if (!articlesSeen.has(codeId)) {
      articlesSeen.add(codeId);
      // Compter combien d'articles de ce code
      const count = (fiche.articles || []).filter(a => a?.code === art.code).length;
      addNode({
        id: codeId,
        label: `${art.code.replace('Code ', 'C. ')}`,
        group: 'article', size: 6 + Math.min(count, 6),
        code: art.code,
        article: count === 1 ? t('graph.tab.art', { article: art.article }) : t('graph.tab.n_articles', { n: count }),
      });
      addLink({
        source: fiche.id, target: codeId,
        label: linkLabel('article_vise'),
        group: 'article_vise',
        color: linkColor('article_vise'),
        width: 1, dashed: true,
      });
    }
  }

  // ── Arrêts cités qualifiés ─────────────────────────────────────────────────
  for (const c of relations.cite_arrets_qualifies || []) {
    if (!c.found || !c.fiche_id) continue;
    const typeKey = c.type || 'application_principe';
    addNode({
      id: c.fiche_id, label: c.ref,
      group: 'cite',
      size: 7 + Math.min((c.citation_count || 0) / 6, 9),
      date: c.decision_date || '', chamber: c.chamber || '',
      solution: c.solution || '', probleme: c.probleme || '',
      citeType: typeKey,
    });
    addLink({
      source: fiche.id, target: c.fiche_id,
      label: linkLabel(typeKey),
      group: typeKey,
      color: linkColor(typeKey),
      width: linkWidth(typeKey),
    });
  }

  // ── Arrêts impactés ────────────────────────────────────────────────────────
  // Jurisprudence antérieure renversée ou modifiée par CET arrêt
  for (const imp of fiche.arrets_impactes || []) {
    if (!imp?.ref) continue;
    // Essayer de trouver la fiche correspondante dans cite_arrets_qualifies
    const matched = (relations.cite_arrets_qualifies || []).find(c => c.ref === imp.ref);
    const nodeId  = matched?.fiche_id || `impacte_${imp.ref}`;
    const impGroup = `impact_${imp.impact || 'revirement'}`;

    if (!matched?.fiche_id) {
      addNode({
        id: nodeId, label: imp.ref,
        group: 'impacte', size: 9,
        impact: imp.impact, explication: imp.explication,
      });
    }
    addLink({
      source: fiche.id, target: nodeId,
      label: linkLabel(impGroup),
      group: impGroup,
      color: linkColor(impGroup),
      width: 3,
    });
  }

  // ── Arrêts citants ─────────────────────────────────────────────────────────
  for (const cb of (relations.cited_by_fiches || []).slice(0, 20)) {
    if (!cb.id) continue;
    addNode({
      id: cb.id, label: cb.number || cb.ecli,
      group: 'cited_by',
      size: 6 + Math.min((cb.importance_score || 0), 9),
      date: cb.decision_date, chamber: cb.chamber, solution: cb.solution,
    });
    addLink({
      source: cb.id, target: fiche.id,
      label: linkLabel('cited_by'), group: 'cited_by',
      color: linkColor('cited_by'), width: 1.5,
    });
  }

  // ── Liens procéduraux résolus ──────────────────────────────────────────────
  for (const lnk of relations.procedure_graph || []) {
    if (!lnk.target_id) continue;
    const ltKey = lnk.link_type || 'procedure';
    addNode({
      id: lnk.target_id, label: lnk.target_number || lnk.target,
      group: 'procedure', size: 9,
      date: lnk.target_decision_date || '', chamber: lnk.target_chamber || '',
      solution: lnk.target_solution || '', probleme: lnk.target_probleme || '',
    });
    addLink({
      source: fiche.id, target: lnk.target_id,
      label: linkLabel(ltKey), group: ltKey,
      color: linkColor(ltKey), width: linkWidth(ltKey),
    });
  }

  // ── Liens procéduraux non résolus (pas d'id Qdrant) ───────────────────────
  let unresolvedIdx = 0;
  for (const lnk of relations.procedure_graph || []) {
    if (lnk.target_id || !lnk.target) continue;
    const nodeId = `unresolved_${unresolvedIdx++}`;
    const ltKey  = lnk.link_type || 'procedure';
    addNode({
      id: nodeId, label: lnk.target,
      group: 'procedure', size: 7,
      probleme: t('graph.tab.not_indexed'),
    });
    addLink({
      source: fiche.id, target: nodeId,
      label: linkLabel(ltKey), group: ltKey,
      color: linkColor(ltKey) + '88', width: 1,
      dashed: true,
    });
  }

  return { nodes, links };
}

// ── Légende ───────────────────────────────────────────────────────────────────

const LEGEND_NODES = [
  { color: '#7c3aed', label: 'graph.legend.node_center' },
  { color: '#1d4ed8', label: 'graph.legend.node_cite' },
  { color: '#059669', label: 'graph.legend.node_cited_by' },
  { color: '#c2410c', label: 'graph.legend.node_procedure' },
  { color: '#b91c1c', label: 'graph.legend.node_impacte' },
  { color: '#d97706', label: 'graph.legend.node_article' },
];

const LEGEND_LINKS = [
  { color: '#ef4444', label: 'graph.legend.link_revirement' },
  { color: '#22c55e', label: 'graph.legend.link_confirmation' },
  { color: '#3b82f6', label: 'graph.legend.link_application' },
  { color: '#a855f7', label: 'graph.legend.link_distinction' },
  { color: '#f97316', label: 'graph.legend.link_cassation' },
  { color: '#34d399', label: 'graph.legend.link_cited_by' },
  { color: '#dc2626', label: 'graph.legend.link_reversal' },
  { color: '#f59e0b', label: 'graph.legend.link_article' },
];

const Legend = () => {
  const { t } = useTranslation();
  return (
  <div className="space-y-2">
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
      {LEGEND_NODES.map(({ color, label }) => (
        <div key={label} className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
          <span className="text-muted-foreground">{t(label)}</span>
        </div>
      ))}
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
      {LEGEND_LINKS.map(({ color, label }) => (
        <div key={label} className="flex items-center gap-1.5">
          <span className="w-5 h-0.5 shrink-0 rounded" style={{ background: color }} />
          <span className="text-muted-foreground">{t(label)}</span>
        </div>
      ))}
    </div>
  </div>
  );
};

// ── Panel article ─────────────────────────────────────────────────────────────

const ArticlePanel = ({ fiche }: { fiche: PublicFiche }) => {
  const { t } = useTranslation();
  const byCode = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const art of fiche.articles || []) {
      if (!art?.code || !art?.article) continue;
      const artAny = art as any;
      if (!map[art.code]) map[art.code] = [];
      const label = artAny.alinea
        ? t('graph.tab.art_alinea', { article: art.article, alinea: artAny.alinea })
        : t('graph.tab.art', { article: art.article });
      if (!map[art.code].includes(label)) map[art.code].push(label);
    }
    return map;
  }, [fiche.articles, t]);

  if (Object.keys(byCode).length === 0) return null;

  return (
    <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 p-3 text-xs">
      <p className="font-semibold text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-1.5">
        <span>§</span> {t('graph.legend.node_article')}
      </p>
      <div className="space-y-1">
        {Object.entries(byCode).map(([code, arts]) => (
          <div key={code}>
            <span className="font-medium text-foreground">{code} · </span>
            <span className="text-muted-foreground">{arts.join(', ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

export const LegalGraphTab = ({ fiche, relations }: LegalGraphTabProps) => {
  const navigate      = useNavigate();
  const { t }         = useTranslation();
  const graphRef      = useRef<any>(null);
  const containerRef  = useRef<HTMLDivElement>(null);
  const hasStabilized = useRef(false);

  const [dims, setDims]           = useState({ w: 700, h: 500 });
  const [bgColor, setBgColor]     = useState('#ffffff');
  const [hovered, setHovered]     = useState<GNode | null>(null);
  const [selected, setSelected]   = useState<GNode | null>(null);
  const [stabilized, setStabilized] = useState(false);

  // Lire la couleur de fond du thème CSS
  useEffect(() => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--background').trim();
    setBgColor(raw ? `hsl(${raw})` : '#ffffff');
  }, []);

  const { nodes, links } = useMemo(
    () => buildGraph(fiche, relations, t),
    [fiche, relations, t],
  );

  // Dimensions responsives
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: Math.max(height, 420) });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Zoom to fit après stabilisation
  useEffect(() => {
    if (stabilized) setTimeout(() => graphRef.current?.zoomToFit(400, 40), 100);
  }, [stabilized]);

  const handleNodeClick = useCallback((node: any) => {
    const n = node as GNode;
    if (n.id === fiche.id) return;
    setSelected(prev => prev?.id === n.id ? null : n);
  }, [fiche.id]);

  const handleNodeHover = useCallback((node: any) => {
    setHovered(node as GNode | null);
    if (containerRef.current)
      containerRef.current.style.cursor = node ? 'pointer' : 'default';
  }, []);

  const paintNode = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const n        = node as GNode & { x: number; y: number };
    if (!isFinite(n.x) || !isFinite(n.y)) return;
    const r        = Math.max(1, n.size);
    const palette  = NODE_COLOR[n.group] ?? NODE_COLOR.cite;
    const isCenter = n.id === fiche.id;
    const isSel    = selected?.id === n.id;
    const isHov    = hovered?.id === n.id;
    const isArt    = n.group === 'article';

    // Glow
    const glowR = r + (isSel ? 8 : isHov ? 6 : isCenter ? 5 : 2);
    const grd   = ctx.createRadialGradient(n.x, n.y, r * 0.4, n.x, n.y, glowR + 4);
    grd.addColorStop(0, palette.glow);
    grd.addColorStop(1, 'transparent');
    ctx.beginPath();
    ctx.arc(n.x, n.y, glowR + 4, 0, 2 * Math.PI);
    ctx.fillStyle = grd;
    ctx.fill();

    // Corps — hexagone pour les articles, cercle pour le reste
    const bodyGrd = ctx.createRadialGradient(n.x - r * 0.25, n.y - r * 0.25, 0, n.x, n.y, r);
    bodyGrd.addColorStop(0, palette.fill);
    bodyGrd.addColorStop(1, palette.stroke + 'cc');

    ctx.beginPath();
    if (isArt) {
      // Hexagone pour les nœuds article
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const px = n.x + r * Math.cos(angle);
        const py = n.y + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else {
      ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
    }
    ctx.fillStyle = bodyGrd;
    ctx.fill();
    ctx.strokeStyle = isSel || isHov ? '#ffffff' : palette.stroke;
    ctx.lineWidth   = isCenter ? 2.5 : isSel || isHov ? 2 : 1;
    ctx.stroke();

    // Anneau pointillé pour le nœud central
    if (isCenter) {
      ctx.beginPath();
      ctx.arc(n.x, n.y, r + 4, 0, 2 * Math.PI);
      ctx.strokeStyle = palette.stroke + '70';
      ctx.lineWidth   = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Icône § pour les articles
    if (isArt && r > 5) {
      ctx.font         = `bold ${Math.max(6, r * 0.9)}px system-ui`;
      ctx.fillStyle    = palette.stroke;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('§', n.x, n.y);
    }

    // Label
    const showLabel = globalScale > 0.45 || isCenter || r > 9 || isSel || isHov;
    if (showLabel) {
      const fontSize = Math.max(3, Math.min(11, 10 / globalScale));
      ctx.font         = `${isCenter ? '600 ' : ''}${fontSize}px system-ui, sans-serif`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'top';
      const lbl        = n.label.length > 20 ? n.label.slice(0, 20) + '…' : n.label;
      const yLabel     = n.y + r + 3;
      const tw         = ctx.measureText(lbl).width;
      const pad        = 2;

      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.beginPath();
      ctx.roundRect?.(n.x - tw / 2 - pad, yLabel - pad, tw + pad * 2, fontSize + pad * 2, 2);
      ctx.fill();

      ctx.fillStyle = isCenter ? palette.stroke : '#e2e8f0';
      ctx.fillText(lbl, n.x, yLabel);
    }
  }, [fiche.id, selected, hovered]);

  // Statistiques
  const stats = useMemo(() => ({
    cites:     (relations.cite_arrets_qualifies || []).filter(c => c.found && c.fiche_id).length,
    citedBy:   relations.cited_by_count ?? 0,
    procedure: (relations.procedure_graph || []).length,
    impactes:  (fiche.arrets_impactes || []).length,
    articles:  (fiche.articles || []).length,
    revirements: (relations.cite_arrets_qualifies || []).filter(c => c.type === 'revirement').length,
  }), [fiche, relations]);

  if (nodes.length < 1 || links.length === 0) {
    return (
      <div className="text-center py-24 text-muted-foreground">
        <p className="text-5xl mb-4">🕸️</p>
        <p className="font-semibold text-foreground mb-1">{t('graph.tab.unavailable_title')}</p>
        <p className="text-sm">{t('graph.tab.unavailable_desc')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Stats bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 text-xs flex-wrap">
          {[
            { val: nodes.length,       label: t('graph.tab.stat_nodes'),       color: 'text-foreground' },
            { val: links.length,       label: t('graph.tab.stat_links'),       color: 'text-foreground' },
            { val: stats.cites,        label: t('graph.tab.stat_cites'),       color: 'text-blue-600 dark:text-blue-400' },
            { val: stats.citedBy,      label: t('graph.tab.stat_cited_by'),    color: 'text-emerald-600 dark:text-emerald-400' },
            { val: stats.impactes,     label: t('graph.tab.stat_impactes'),    color: 'text-red-600 dark:text-red-400', hide: stats.impactes === 0 },
            { val: stats.revirements,  label: t('graph.tab.stat_revirements'), color: 'text-red-500 dark:text-red-300', hide: stats.revirements === 0 },
            { val: stats.articles,     label: t('graph.tab.stat_articles'),    color: 'text-amber-600 dark:text-amber-400', hide: stats.articles === 0 },
          ].filter(s => !s.hide).map(({ val, label, color }) => (
            <span key={label} className="text-muted-foreground">
              <strong className={color}>{val}</strong> {label}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => graphRef.current?.zoomToFit(400, 40)}
            className="text-xs px-3 py-1.5 rounded-lg border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
          >
            ⊡ {t('graph.tab.center')}
          </button>
          <button
            onClick={() => {
              hasStabilized.current = false;
              setStabilized(false);
              graphRef.current?.d3ReheatSimulation();
            }}
            className="text-xs px-3 py-1.5 rounded-lg border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
          >
            ↺ {t('graph.tab.reorganize')}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="relative rounded-xl border bg-background overflow-hidden"
        style={{ height: '520px' }}
      >
        <ForceGraph2D
          ref={graphRef}
          width={dims.w}
          height={dims.h}
          graphData={{ nodes: nodes as any[], links: links as any[] }}
          nodeId="id"
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          linkColor={(l: any) => (l as GLink).color}
          linkWidth={(l: any) => (l as GLink).width ?? 1.5}
          linkLineDash={(l: any) => (l as GLink).dashed ? [4, 3] : null}
          linkDirectionalArrowLength={4}
          linkDirectionalArrowRelPos={1}
          linkCurvature={0.15}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          onEngineStop={() => {
            if (!hasStabilized.current) {
              hasStabilized.current = true;
              setStabilized(true);
            }
          }}
          d3AlphaDecay={0.03}
          d3VelocityDecay={0.4}
          cooldownTime={3000}
          backgroundColor={bgColor}
        />

        {/* Hint simulation */}
        {!stabilized && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 text-xs text-muted-foreground/40 pointer-events-none select-none">
            {t('graph.tab.simulating')}
          </div>
        )}

        {/* Panel nœud sélectionné */}
        {selected && (
          <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:max-w-xs bg-card/95 backdrop-blur-sm border rounded-xl p-4 shadow-2xl z-10">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: NODE_COLOR[selected.group]?.stroke ?? '#6b7280' }}
                />
                <p className="font-semibold text-sm text-foreground truncate">{selected.label}</p>
              </div>
              <button onClick={() => setSelected(null)}
                className="text-muted-foreground hover:text-foreground shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nœud article */}
            {selected.group === 'article' && (
              <>
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">
                  {t('graph.tab.code_label')} · {selected.code}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  {(fiche.articles || [])
                    .filter(a => a?.code === selected.code)
                    .map(a => { const aa = a as any; return aa.alinea ? t('graph.tab.art_alinea', { article: a.article, alinea: aa.alinea }) : t('graph.tab.art', { article: a.article }); })
                    .join(', ')
                  }
                </p>
              </>
            )}

            {/* Nœud arrêt */}
            {selected.group !== 'article' && (
              <>
                {selected.citeType && (
                  <span
                    className="inline-block text-xs px-2 py-0.5 rounded-full mb-2 font-medium"
                    style={{
                      background: (LINK_COLOR[selected.citeType] ?? '#94a3b8') + '25',
                      color: LINK_COLOR[selected.citeType] ?? '#94a3b8',
                    }}
                  >
                    {LINK_LABEL[selected.citeType] ? t(LINK_LABEL[selected.citeType]) : selected.citeType}
                  </span>
                )}
                {selected.impact && (
                  <span className="inline-block text-xs px-2 py-0.5 rounded-full mb-2 font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                    {t('graph.tab.impact', { impact: selected.impact })}
                  </span>
                )}
                {selected.date && (
                  <p className="text-xs text-muted-foreground mb-1">
                    {selected.date}{selected.chamber ? ` · ${selected.chamber}` : ''}
                  </p>
                )}
                {selected.solution && (
                  <p className="text-xs font-medium mb-2 text-amber-600 dark:text-amber-400">
                    {selected.solution}
                  </p>
                )}
                {(selected.explication || selected.probleme) && (
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-3 leading-relaxed">
                    {selected.explication || selected.probleme}
                  </p>
                )}
                {selected.id && !selected.id.startsWith('unresolved_') && !selected.id.startsWith('impacte_') && (
                  <button
                    onClick={() => navigate(`/jurisprudence/${selected.slug || selected.id}`)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors w-full text-center font-medium"
                  >
                    {t('graph.tab.open_fiche')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Articles visés (panel texte) */}
      <ArticlePanel fiche={fiche} />

      {/* Légende */}
      <Legend />

      <p className="text-xs text-muted-foreground">
        💡 {t('graph.tab.hint')}
      </p>
    </div>
  );
};